'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { mergeTickers, type PairTicker, parseTickerFrame } from '@/lib/api/futures/client';
import { LAST_DAY_TICKERS_QK } from '@/lib/api/futures/queryKeys';
import { WS_ENDPOINTS } from '@/lib/config/websocket';
import { useWebSocket } from '@/lib/hooks/useWebSocket';
import type { AnyWebSocketMessage } from '@/lib/types/websocket';

interface FuturesTickerStream {
  /** Pairs to stream; pass a memoized array, it is re-subscribed on change. */
  pairIds?: number[];
  /** Also stream every pair, through the socket's `{ act, all: true }` wildcard. */
  subscribeAll?: boolean;
  /** Hold the connection with nothing subscribed, so a wildcard toggle needs no reconnect. */
  stayConnected?: boolean;
}

const NO_PAIRS: number[] = [];
/** The wildcard sends ~150 frames/s: a cache write per frame would re-render every reader as often. */
const FLUSH_MS = 250;

/**
 * Streams `/ws/ticker/24h` into the `/candles/lastDay` cache, batched into one
 * write per `FLUSH_MS`. Mirrors the trade zone's `useTicker24hSocket` options.
 */
export function useFuturesTickerWebSocket({
  pairIds = NO_PAIRS,
  subscribeAll = false,
  stayConnected = false,
}: FuturesTickerStream) {
  const subscribedRef = useRef(new Set<number>());
  const isAllSubscribedRef = useRef(false);
  const pendingRef = useRef(new Map<number, PairTicker>());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();

  const flush = useCallback(() => {
    flushTimerRef.current = null;
    const updates = pendingRef.current;
    pendingRef.current = new Map();
    // Keep the REST read's age: per-pair frames cover only some pairs, so readers
    // of the rest (header search) still re-read the snapshot on their own schedule.
    const updatedAt = queryClient.getQueryState(LAST_DAY_TICKERS_QK)?.dataUpdatedAt;
    queryClient.setQueryData<PairTicker[]>(
      LAST_DAY_TICKERS_QK,
      (tickers) => tickers && mergeTickers(tickers, updates),
      { updatedAt },
    );
  }, [queryClient]);

  const handleMessage = useCallback(
    (msg: AnyWebSocketMessage) => {
      const ticker = parseTickerFrame(msg);
      if (!ticker) return;
      pendingRef.current.set(ticker.pairId, ticker);
      flushTimerRef.current ??= setTimeout(flush, FLUSH_MS);
    },
    [flush],
  );

  const isWanted = stayConnected || subscribeAll || pairIds.length > 0;
  const { send, readyState } = useWebSocket({
    url: isWanted ? WS_ENDPOINTS.futuresTicker : undefined,
    onMessage: handleMessage,
  });

  // A closed socket forgets its subscriptions; an open one gets the added pairs and drops the removed.
  useEffect(() => {
    const subscribed = subscribedRef.current;
    if (readyState !== WebSocket.OPEN) {
      subscribed.clear();
      return;
    }
    for (const pairId of pairIds) {
      if (subscribed.has(pairId)) continue;
      send(JSON.stringify({ act: 'subscribe', pi: pairId }));
      subscribed.add(pairId);
    }
    for (const pairId of subscribed) {
      if (pairIds.includes(pairId)) continue;
      send(JSON.stringify({ act: 'unsubscribe', pi: pairId }));
      subscribed.delete(pairId);
    }
  }, [pairIds, readyState, send]);

  // The wildcard toggles on its own, so the per-pair subscriptions outlive it.
  useEffect(() => {
    if (readyState !== WebSocket.OPEN) {
      isAllSubscribedRef.current = false;
      return;
    }
    if (subscribeAll === isAllSubscribedRef.current) return;
    send(JSON.stringify({ act: subscribeAll ? 'subscribe' : 'unsubscribe', all: true }));
    isAllSubscribedRef.current = subscribeAll;
  }, [subscribeAll, readyState, send]);

  useEffect(
    () => () => {
      if (flushTimerRef.current === null) return;
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    },
    [],
  );

  return { isStreaming: readyState === WebSocket.OPEN };
}
